import { useState, useEffect } from 'react';
import './Jobs.scss';
import { useNavigate, useParams } from 'react-router';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import ReactMarkdown from 'react-markdown';
import Loader from '../../components/Loader';
import { format } from 'date-fns';
import JobApplicationForm from './JobApplicationForm';

const JOBS_API_URL = process.env.REACT_APP_JOBS_API_URL || 'https://jobs.thepioneers.ai';
const VMS_JOB_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const Library = () => {
  const navigate = useNavigate();
  const id = decodeURIComponent(useParams().id);
  const [data, setData] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const controller = new AbortController();

    const getJobDetails = async () => {
      setIsLoading(true);
      setError('');
      setData(undefined);

      try {
        const encodedId = encodeURIComponent(id);
        let response = await fetch(`${JOBS_API_URL}/vms/jobs/${encodedId}/description`, {
          signal: controller.signal,
        });

        // The current list response still contains legacy slug IDs. Keep the
        // detail page working until vms_job_id is present in every list item.
        if (response.status === 404 && !VMS_JOB_ID_PATTERN.test(id)) {
          response = await fetch(`${JOBS_API_URL}/job/${encodedId}/description`, {
            signal: controller.signal,
          });
        }

        if (!response.ok) {
          if (response.status === 404) throw new Error('JOB_NOT_FOUND');
          throw new Error(`Failed to fetch job details: ${response.status}`);
        }

        const result = await response.json();
        if (!result.Items) throw new Error('JOB_UNAVAILABLE');
        setData(result.Items);
      } catch (requestError) {
        if (requestError.name === 'AbortError') return;
        console.error('Error fetching job details:', requestError);
        setError(
          requestError.message === 'JOB_NOT_FOUND' || requestError.message === 'JOB_UNAVAILABLE'
            ? 'This job is no longer available.'
            : 'We could not load this job. Please try again later.',
        );
      } finally {
        if (!controller.signal.aborted) setIsLoading(false);
      }
    };

    getJobDetails();
    return () => controller.abort();
  }, [id]);

  const contentWithoutReadySection = data?.content
    ?.replace(/\n?###\s+Ready to (?:Make Your Mark|Apply)\??[\s\S]*$/i, '')
    .trim();

  return (
    <div className='page'>
      <Header />
      <div className='libraryPage jobDetailsPage'>
        <div className='libraryBackLink' onClick={() => navigate('/jobs')}>
          <svg width='11' height='11' viewBox='0 0 11 11' fill='none' xmlns='http://www.w3.org/2000/svg'>
            <path
              d='M5.33073 9.99833L0.664062 5.33166M0.664062 5.33166L5.33073 0.664993M0.664062 5.33166H9.9974'
              stroke='#3A6376'
              stroke-width='1.33'
              stroke-linecap='round'
              stroke-linejoin='round'
            />
          </svg>
          Back to All Jobs
        </div>
        {isLoading ? (
          <div className='loader'>
            <Loader />
          </div>
        ) : error ? (
          <div className='libraryEmpty'>
            <span className='libraryEmptyText'>{error}</span>
            <button type='button' className='libraryEmptyButton libraryListItemConterolForm' onClick={() => navigate('/jobs')}>
              Back to all jobs
            </button>
          </div>
        ) : (
          <>
            <span className='libraryTitle'>{data?.title}</span>
            <span className='libraryListItemDetails'>
              <span className='libraryListItemLocation'>{data.location} •</span>
              <span className='libraryListItemDate'>{format(new Date(data.created_time), 'MMM dd yyyy')} </span>
            </span>
            <div className='candidatePage'>
              <div className='candidatePageContent'>
                <ReactMarkdown>{contentWithoutReadySection}</ReactMarkdown>
              </div>
            </div>
          </>
        )}
      </div>
      {!isLoading && !error && data && (
        <section className='jobApplicationSection'>
          <div className='jobApplicationSectionInner'>
            <JobApplicationForm job={data} className='jobApplicationFormInline' />
          </div>
        </section>
      )}
      <Footer />
    </div>
  );
};

export default Library;
